'use client';
import { createTheme, lighten, type ThemeOptions } from '@mui/material/styles';
import siteConfig from '@/config/site';
import { DISPLAY_FONT, TEXT_FONT } from './fonts';

/**
 * « Une vitrine allumée sur une rue sombre » — the theme from docs/site-design.md.
 *
 * The whole look lives here so no component ever needs a hex code:
 *  - the palette is the enamel sign panel (`background.default`), the plaque
 *    (`background.paper`), the pâtisserie counter (`surfaceAlt`), the steel racks
 *    (`divider`), the awning fascia (`brique`), the gold trays (`or`), and the
 *    red of the rule under the wordmark (`primary`, from site.ts → brand);
 *  - the type is a WIDTH contrast: Saira Condensed caps for display over Archivo
 *    for text, exactly the contrast on the shop's own sign;
 *  - radius is 2px everywhere and **there are no shadows at all** — depth is a
 *    surface step plus a 1px line, which is how the sign sits on the fascia.
 */

declare module '@mui/material/styles' {
  interface Palette {
    /** #2C1E18 — le comptoir. The pâtisserie band only. */
    surfaceAlt: string;
    /** #6E3320 — the awning fascia. Washes and gradient stops only. */
    brique: string;
    /** #C9A227 — the gold pastry trays. Pâtisserie band only. */
    or: string;
  }
  interface PaletteOptions {
    surfaceAlt?: string;
    brique?: string;
    or?: string;
  }
}

const NUIT = '#171110';
const PLAQUE = '#221917';
const COMPTOIR = '#2C1E18';
const LIGNE = '#3A2B25';
const BRIQUE = '#6E3320';
const OR = '#C9A227';
const TEXTE = '#F4EDE7';
const TEXTE_MUTED = '#B3A69E';
const ROUGE = siteConfig.brand.primary;

/** Condensed caps need POSITIVE tracking. Shared by every display level. */
const displayCaps = {
  fontFamily: DISPLAY_FONT,
  textTransform: 'uppercase',
} as const;

const base: ThemeOptions = {
  palette: {
    mode: siteConfig.brand.mode,
    primary: { main: ROUGE, contrastText: '#FFFFFF' },
    secondary: { main: siteConfig.brand.secondary, contrastText: '#171110' },
    background: { default: NUIT, paper: PLAQUE },
    text: { primary: TEXTE, secondary: TEXTE_MUTED },
    divider: LIGNE,
    surfaceAlt: COMPTOIR,
    brique: BRIQUE,
    or: OR,
  },
  shape: { borderRadius: 2 },
  // No box-shadow anywhere on this site — stated once, never overridden.
  shadows: Array.from({ length: 25 }, () => 'none') as ThemeOptions['shadows'],
  typography: {
    fontFamily: TEXT_FONT,
    fontSize: 16,
    h1: {
      ...displayCaps,
      fontWeight: 800,
      letterSpacing: '0.02em',
      lineHeight: 0.95,
      fontSize: 'clamp(2rem, 10vw, 4rem)',
    },
    h2: {
      ...displayCaps,
      fontWeight: 800,
      letterSpacing: '0.04em',
      lineHeight: 1.05,
      fontSize: 'clamp(1.75rem, 5vw, 2.5rem)',
    },
    h3: { ...displayCaps, fontWeight: 600, letterSpacing: '0.04em', lineHeight: 1.2, fontSize: '1.25rem' },
    h4: { ...displayCaps, fontWeight: 600, letterSpacing: '0.04em', lineHeight: 1.2, fontSize: '1.125rem' },
    h5: { ...displayCaps, fontWeight: 600, letterSpacing: '0.04em', lineHeight: 1.25, fontSize: '1rem' },
    h6: { ...displayCaps, fontWeight: 600, letterSpacing: '0.04em', lineHeight: 1.25, fontSize: '1rem' },
    body1: { fontSize: '1rem', lineHeight: 1.6 },
    body2: { fontSize: '0.875rem', lineHeight: 1.5 },
    /** The small caps label of the site: Saira Condensed 500 at +0.14em. */
    overline: {
      ...displayCaps,
      fontWeight: 500,
      letterSpacing: '0.14em',
      lineHeight: 1.4,
      fontSize: '0.8125rem',
    },
    button: { fontFamily: TEXT_FONT, fontWeight: 600, textTransform: 'none', letterSpacing: 0 },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: { backgroundColor: NUIT, color: TEXTE },
        // Focus is never removed: 2px red outline at 2px offset, everywhere.
        '*:focus-visible': { outline: `2px solid ${ROUGE}`, outlineOffset: '2px' },
        '::selection': { backgroundColor: ROUGE, color: '#FFFFFF' },
      },
    },
    MuiPaper: {
      styleOverrides: { root: { backgroundImage: 'none', boxShadow: 'none' } },
    },
    MuiCard: {
      styleOverrides: { root: { backgroundImage: 'none', boxShadow: 'none', borderRadius: 2 } },
    },
    MuiAppBar: {
      defaultProps: { elevation: 0, color: 'inherit' },
      styleOverrides: { root: { boxShadow: 'none', backgroundImage: 'none' } },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 2,
          boxShadow: 'none',
          minHeight: 44,
          paddingInline: 20,
          transition: 'background-color 150ms ease-out',
          // No lift, no shadow, no scale: the fill just lightens.
          '&:hover': { boxShadow: 'none' },
        },
        containedPrimary: {
          color: '#FFFFFF',
          '&:hover': { backgroundColor: lighten(ROUGE, 0.08) },
        },
      },
    },
    // The interaction language of the whole site: a 2px red rule drawn in from
    // the left on hover AND focus. Nothing lifts, nothing scales.
    MuiLink: {
      defaultProps: { underline: 'none' },
      styleOverrides: {
        root: {
          position: 'relative',
          textDecoration: 'none',
          '&:hover': { textDecoration: 'none' },
          '&::after': {
            content: '""',
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: '-2px',
            height: '2px',
            backgroundColor: ROUGE,
            transform: 'scaleX(0)',
            transformOrigin: 'left',
            transition: 'transform 150ms ease-out',
          },
          '&:hover::after, &:focus-visible::after': { transform: 'scaleX(1)' },
          '@media (prefers-reduced-motion: reduce)': {
            '&::after': { transition: 'none' },
          },
        },
      },
    },
  },
};

export const theme = createTheme(base);
export default theme;
